// E-Invoicing — Kompositionswurzel des Moduls (Muster: PlantMaintenanceView).
// Pipeline-Navigation (Candis/Pagero-Muster aus der UX-Recherche): Dashboard ->
// Posteingang -> Postausgang -> Laender & Formate. Interne Navigation per useState
// (kein react-router im Monolithen). Props aus der App-Registry-Fast-Path:
// { user, onBack, showSuccess, showError }.

import React, { useEffect, useState } from 'react';
import { Receipt, ArrowLeft, LayoutDashboard, Inbox, Send, Globe2 } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { getRegimes, getFormats, getPlants, getCapabilities } from './api';
import DashboardScreen from './components/DashboardScreen';
import InvoiceList from './components/InvoiceList';
import InvoiceDetail from './components/InvoiceDetail';
import OutboundScreen from './components/OutboundScreen';
import CoverageMatrix from './components/CoverageMatrix';

const EInvoicingView = ({ user, onBack, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Interne Navigation: { name: 'dashboard'|'inbox'|'outbound'|'coverage'|'detail', ... }
  const [nav, setNav] = useState({ name: 'dashboard' });
  const [regimes, setRegimes] = useState([]);
  const [formats, setFormats] = useState([]);
  const [sapConnected, setSapConnected] = useState(null); // null = unbekannt (gilt als verbunden); false nur laut Server (SaaS ohne SAP)
  const [plants, setPlants] = useState([]); // dem User sichtbare Werke (fuer Filter + manuelle Zuordnung)

  useEffect(() => {
    getRegimes(user).then(setRegimes).catch(() => {});
    getFormats(user).then(setFormats).catch(() => {});
    getCapabilities(user).then((c) => setSapConnected(c.sapConnected === false ? false : true)).catch(() => {});
    getPlants(user).then((r) => setPlants(r.plants || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openInvoice = (id) => setNav((prev) => ({ name: 'detail', invoiceId: id, from: prev.name === 'detail' ? prev.from : prev.name }));
  const openList = (direction, filters) => setNav({ name: direction === 'outbound' ? 'outbound' : 'inbox', filters });

  const TABS = [
    { id: 'dashboard', Icon: LayoutDashboard, label: t('einvoicing.navDashboard') },
    { id: 'inbox', Icon: Inbox, label: t('einvoicing.navInbox') },
    { id: 'outbound', Icon: Send, label: t('einvoicing.navOutbound') },
    { id: 'coverage', Icon: Globe2, label: t('einvoicing.navCoverage') },
  ];

  const body = () => {
    switch (nav.name) {
      case 'inbox':
        return <InvoiceList user={user} isDarkMode={isDarkMode} t={t} direction="inbound"
          initialFilters={nav.filters || {}} plants={plants} onOpenInvoice={openInvoice}
          showSuccess={showSuccess} showError={showError} />;
      case 'outbound':
        return <OutboundScreen user={user} isDarkMode={isDarkMode} t={t} plants={plants} sapConnected={sapConnected}
          onOpenInvoice={openInvoice} showSuccess={showSuccess} showError={showError} />;
      case 'coverage':
        return <CoverageMatrix isDarkMode={isDarkMode} t={t} regimes={regimes} formats={formats} />;
      case 'detail':
        return <InvoiceDetail user={user} isDarkMode={isDarkMode} t={t} invoiceId={nav.invoiceId}
          formats={formats} regimes={regimes} plants={plants} sapConnected={sapConnected}
          onBack={() => setNav({ name: nav.from || 'dashboard' })}
          showSuccess={showSuccess} showError={showError} />;
      default:
        return <DashboardScreen user={user} isDarkMode={isDarkMode} t={t} regimes={regimes} plants={plants}
          onOpenList={openList} onOpenInvoice={openInvoice} showError={showError} />;
    }
  };

  return (
    // min-w-0: als Flex-Kind der App-Shell schrumpft dieser Container sonst nicht unter die
    // intrinsische Breite seines Inhalts (Flex-Default min-width:auto) -> die ganze Seite
    // scrollt auf schmalen Fenstern horizontal. Innere overflow-x-auto-Kaesten scrollen weiter selbst.
    <div className={`flex-1 min-w-0 min-h-screen ${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
      : 'bg-[#F8FAFC]'
    }`}>
      {/* Header (Monolith-Idiom: Glas-Band, violetter Modul-Akzent) */}
      <div className="relative">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
          : 'bg-white border-b border-slate-200'
        }`}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center gap-4 flex-wrap">
            <button onClick={onBack}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isDarkMode
                ? 'bg-white/10 hover:bg-white/15 text-white/80 hover:text-white border border-white/10'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900'
              }`}>
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </button>

            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDarkMode
                ? 'bg-gradient-to-br from-violet-500/70 via-violet-600/60 to-violet-700/70 shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/20'
                : 'bg-violet-100'
              }`}>
                <Receipt className={`w-5 h-5 ${isDarkMode ? 'text-white/90' : 'text-violet-600'}`} />
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDarkMode
                  ? 'bg-gradient-to-r from-white via-violet-200 to-white bg-clip-text text-transparent'
                  : 'text-slate-900'
                }`}>
                  {t('einvoicing.title')}
                </h1>
              </div>
            </div>

            {/* Pipeline-Navigation */}
            <nav className="flex gap-1.5 ml-auto flex-wrap">
              {TABS.map(({ id, Icon, label }) => {
                const active = nav.name === id || (nav.name === 'detail' && nav.from === id);
                return (
                  <button key={id} onClick={() => setNav({ name: id })}
                    className={`flex items-center justify-center gap-2 min-h-[40px] px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${active
                      ? (isDarkMode ? 'bg-violet-600/70 text-white border border-violet-400/30' : 'bg-violet-600 text-white')
                      : (isDarkMode ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100')
                    }`}>
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {body()}
      </div>
    </div>
  );
};

export default EInvoicingView;
